import { FormLabel } from '@src/components/ui';
import { useLazyGetRoleListQuery } from '@src/services/role/roleList/roleListApiSlice';
import { Select } from 'antd';
import { useEffect, useMemo, useRef } from 'react';
import { Control, Controller, useFormState } from 'react-hook-form';

interface FormSelectRoleProps {
    label: string;
    keyName: string;
    control: Control<any>;
    className?: string;
    formProps: any;
    registerOptions: any;
}

export const FormSelectRole = ({
    label,
    keyName,
    control,
    className,
    formProps,
    registerOptions,
}: FormSelectRoleProps) => {
    const onChangeRef = useRef<((val: any) => void) | null>(null);
    const prevAccountIdRef = useRef<string | undefined>(formProps?.accountId);
    const [getRoleList, { data, isLoading, isFetching }] =
        useLazyGetRoleListQuery();
    useEffect(() => {
        if (
            !formProps?.requireAccountId ||
            (formProps?.requireAccountId && formProps?.accountId)
        ) {
            getRoleList({
                accountId: formProps?.accountId || undefined,
            });

            if (
                prevAccountIdRef.current !== undefined &&
                prevAccountIdRef.current !== formProps?.accountId
            ) {
                onChangeRef.current?.(null);
            }
        } else if (formProps?.requireAccountId && !formProps?.accountId) {
            onChangeRef.current?.(null);
        }

        prevAccountIdRef.current = formProps?.accountId;
    }, [formProps?.accountId, formProps?.requireAccountId]);

    const roleOptions = useMemo(() => {
        if (formProps?.requireAccountId && !formProps?.accountId) {
            return formProps.customOption ? [formProps.customOption] : [];
        }

        const baseOptions =
            data?.data?.items?.map((item) => ({
                label: item.name,
                value: item.id,
            })) || [];

        if (formProps.customOption) {
            return [...baseOptions, formProps.customOption];
        }

        return baseOptions;
    }, [
        data,
        formProps?.customOption,
        formProps?.requireAccountId,
        formProps?.accountId,
    ]);

    const { touchedFields, isSubmitted } = useFormState({ control });

    return (
        <div className={className}>
            <Controller
                control={control}
                name={keyName}
                render={({
                    field: { onChange, value },
                    fieldState: { error: fieldStateError },
                }) => {
                    // Only show error if field has been touched or form submitted
                    const error = fieldStateError;
                    const showError = Boolean(
                        error && (touchedFields[keyName] || isSubmitted)
                    );
                    const handleChange = (val: string | null | undefined) => {
                        if (val === null || val === undefined) {
                            onChange(null);
                            formProps?.onChange?.(null);
                            return;
                        }

                        const selectedOption = roleOptions.find(
                            (opt) => opt.value === val
                        );

                        if (selectedOption) {
                            onChange(selectedOption);
                            formProps?.onChange?.(selectedOption);
                        }
                    };
                    onChangeRef.current = onChange;
                    return (
                        <>
                            {label && (
                                <FormLabel
                                    label={label}
                                    required={
                                        typeof registerOptions?.required ===
                                        'object'
                                            ? Boolean(
                                                  registerOptions.required.value
                                              )
                                            : Boolean(registerOptions?.required)
                                    }
                                />
                            )}

                            <Select
                                size={formProps.size}
                                options={roleOptions}
                                value={value?.value ?? undefined}
                                onChange={handleChange}
                                filterOption={(input, option) => {
                                    return (
                                        typeof option?.label === 'string'
                                            ? option.label
                                            : ''
                                    )
                                        .toLowerCase()
                                        .includes(input.toLowerCase());
                                }}
                                placeholder="Select a role"
                                loading={isLoading || isFetching}
                                disabled={isLoading}
                                showSearch
                                style={{ width: '100%' }}
                                mode={
                                    formProps?.isMulti ? 'multiple' : undefined
                                }
                                allowClear={formProps?.allowClear}
                            />
                            {showError && error?.message && (
                                <div className="text-red-500 text-sm mt-0">
                                    {error.message}
                                </div>
                            )}
                        </>
                    );
                }}
            />
        </div>
    );
};
